import { type FC } from "react";
import { isPositiveScanResult, type OriginCheckResult } from "~/features/scanDmc/types/ScanDMC";
import Modal from "~/common/components/Modal";
import ScanResultAlert from "~/features/scanDmc/components/ScanResultAlert/ScanResultAlert";
import { Button, Stack, styled, Typography } from "~/common/mui";
import CustomModalHeader from "~/features/scanDmc/components/CustomModalHeader/CustomModalHeader";
import { useTranslation } from "react-i18next";
import { IMG_EMPTY_STATE } from "~/utils/url/staticResources";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { BackLink, Link } from "~/common/components/Link";

const ModalWithBorder = styled("div")(({ theme }) => ({
  "& .scha__modal__footer": {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

interface ErrorOverlayProps {
  onReturn: () => void;
  onContactUs: () => void;
  scanResult: OriginCheckResult;
}

const ErrorOverlay: FC<ErrorOverlayProps> = ({ scanResult, onReturn, onContactUs }) => {
  const { t } = useTranslation();

  const productIsUnavailable = isPositiveScanResult(scanResult) && !scanResult.productCode;

  let footer;
  if (productIsUnavailable) {
    footer = (
      <Stack sx={{ alignItems: "center", gap: 3, pb: 1 }}>
        <Button fullWidth onClick={onContactUs}>
          {t("page.scandmc.unavailable.button.contactUs")}
        </Button>
        <Link href={HOMEPAGE}>
          {t("page.scandmc.unavailable.button.home")}
        </Link>
      </Stack>
    );
  } else {
    footer = (
      <Button onClick={onReturn} fullWidth>
        {t("page.scandmc.form.button.close")}
      </Button>
    );
  }

  return (
    <ModalWithBorder>
      <Modal headerOverride={<CustomModalHeader />} footerChildren={footer} onClose={onReturn}>
        {productIsUnavailable && (
          <BackLink className="mb-24" onClick={onReturn}>
            {t("page.scandmc.back")}
          </BackLink>
        )}
        <Stack sx={{ gap: 4, pt: 2 }}>
          <ScanResultAlert scanResult={scanResult.type} openOverlay={onContactUs} />
          {productIsUnavailable && <ProductUnavailableMessage />}
        </Stack>
      </Modal>
    </ModalWithBorder>
  );
};

const ProductUnavailableMessage = () => {
  const { t } = useTranslation();
  return (
    <Stack sx={{ alignItems: "center", gap: 4 }}>
      <Typography variant="headlineSmall" sx={{ margin: 0, textAlign: "center" }}>
        {t("page.scandmc.unavailable.message")}
      </Typography>
      <img src={IMG_EMPTY_STATE} alt={t("page.scandmc.unavailable.message")} />
    </Stack>
  );
};

export default ErrorOverlay;
